import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit2, ExternalLink, Link2, Megaphone, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';

import {
  createAnnouncement,
  deleteAnnouncement,
  getAdminAnnouncements,
  updateAnnouncement,
} from '../services/api';
import { Button } from './ui/button';

const initialForm = {
  title: '',
  body: '',
  link_label: '',
  link_url: '',
  open_in_new_tab: false,
  is_important: false,
  is_active: true,
  display_order: 0,
  start_at: '',
  end_at: '',
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const formatDisplayDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AdminAnnouncementManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadAnnouncements = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAdminAnnouncements();
      setAnnouncements(data || []);
    } catch (err) {
      console.error(err);
      setError('お知らせの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const payload = {
        ...formData,
        display_order: Number(formData.display_order) || 0,
        start_at: formData.start_at ? new Date(formData.start_at).toISOString() : null,
        end_at: formData.end_at ? new Date(formData.end_at).toISOString() : null,
      };

      if (editingId) {
        await updateAnnouncement(editingId, payload);
        setMessage('お知らせを更新しました。');
      } else {
        await createAnnouncement(payload);
        setMessage('お知らせを追加しました。');
      }

      setFormData(initialForm);
      setEditingId(null);
      loadAnnouncements();
    } catch (err) {
      console.error(err);
      setError('保存に失敗しました。入力内容をご確認ください。');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || '',
      body: item.body || '',
      link_label: item.link_label || '',
      link_url: item.link_url || '',
      open_in_new_tab: Boolean(item.open_in_new_tab),
      is_important: Boolean(item.is_important),
      is_active: Boolean(item.is_active),
      display_order: item.display_order ?? 0,
      start_at: toDateTimeLocal(item.start_at || item.created_at),
      end_at: toDateTimeLocal(item.end_at),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('このお知らせを削除しますか？')) return;
    setError('');
    setMessage('');
    try {
      await deleteAnnouncement(id);
      if (editingId === id) {
        setEditingId(null);
        setFormData(initialForm);
      }
      setMessage('削除しました。');
      loadAnnouncements();
    } catch (err) {
      console.error(err);
      setError('削除に失敗しました。');
    }
  };

  const handleReset = () => {
    setEditingId(null);
    setFormData(initialForm);
    setMessage('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin-dashboard"
              className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
              aria-label="管理ダッシュボードに戻る"
            >
              <ArrowLeft size={24} />
            </Link>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin Console</p>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Megaphone className="w-6 h-6 text-blue-600" />
                お知らせ管理
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" onClick={loadAnnouncements}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              再読み込み
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{editingId ? 'お知らせを編集' : '新規お知らせを作成'}</h2>
              <p className="text-sm text-gray-500">ダッシュボードに表示するお知らせを作成・更新します。</p>
            </div>
            {editingId && (
              <Button variant="outline" size="sm" className="border-gray-300 text-gray-700" onClick={handleReset}>
                編集をキャンセル
              </Button>
            )}
          </div>

          {error && <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          {message && <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{message}</div>}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">タイトル</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  required
                  maxLength={200}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">本文</label>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  rows={5}
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">リンクラベル</label>
                  <input
                    type="text"
                    value={formData.link_label}
                    onChange={(e) => setFormData({ ...formData, link_label: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder="例: 詳細を見る"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">リンクURL</label>
                  <input
                    type="text"
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder="/payment または https://..."
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">表示開始</label>
                  <input
                    type="datetime-local"
                    value={formData.start_at}
                    onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">表示終了</label>
                  <input
                    type="datetime-local"
                    value={formData.end_at}
                    onChange={(e) => setFormData({ ...formData, end_at: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">表示優先度</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    min={0}
                    step={1}
                  />
                  <p className="text-xs text-gray-500 mt-1">大きいほど上に表示されます。</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">リンクの開き方</label>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <input
                      id="open_in_new_tab"
                      type="checkbox"
                      checked={formData.open_in_new_tab}
                      onChange={(e) => setFormData({ ...formData, open_in_new_tab: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="open_in_new_tab">新しいタブで開く</label>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  公開する
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={formData.is_important}
                    onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  重要表示
                </label>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button type="submit" className="bg-blue-600 text-white hover:bg-blue-700" disabled={saving}>
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> 保存中...
                    </>
                  ) : (
                    <>
                      {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      {editingId ? '更新する' : '追加する'}
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" className="border-gray-300 text-gray-700" onClick={handleReset} disabled={saving}>
                  入力をリセット
                </Button>
              </div>
            </div>
          </form>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">登録済みのお知らせ</h2>
            <span className="text-sm text-gray-500">{announcements.length} 件</span>
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-500">読み込み中...</div>
          ) : announcements.length === 0 ? (
            <div className="py-10 text-center text-gray-500">まだお知らせがありません。</div>
          ) : (
            <div className="space-y-3">
              {announcements.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          優先度 {item.display_order ?? 0}
                        </span>
                        {item.is_active ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                            公開中
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                            非公開
                          </span>
                        )}
                        {item.is_important && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            重要
                          </span>
                        )}
                        {item.open_in_new_tab && (
                          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            新しいタブ
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-700 whitespace-pre-line">{item.body}</p>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>開始: {formatDisplayDate(item.start_at) || '―'} / 終了: {formatDisplayDate(item.end_at) || '―'}</div>
                        <div>作成: {formatDisplayDate(item.created_at)}</div>
                        {item.link_url && (
                          <div className="flex items-center gap-1 text-blue-700">
                            <Link2 className="w-4 h-4" />
                            <a
                              href={item.link_url}
                              target={item.open_in_new_tab || item.link_url.startsWith('http') ? '_blank' : undefined}
                              rel="noreferrer"
                              className="hover:underline"
                            >
                              {item.link_label || item.link_url}
                            </a>
                            {(item.open_in_new_tab || item.link_url.startsWith('http')) && <ExternalLink className="w-4 h-4" />}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="border-gray-300 text-gray-700" onClick={() => handleEdit(item)}>
                        <Edit2 className="w-4 h-4 mr-1" /> 編集
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> 削除
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminAnnouncementManager;
